/**
 * Core Data Models for Expense Splitter
 * 
 * Following dev guide principles:
 * - Pure types and interfaces
 * - Versioned schema for migrations
 * - Deterministic structure
 */

// Schema version for migrations
export const SCHEMA_VERSION = '1.0.0';

// Unique identifier type
export type ID = string;

/**
 * Participant in a group
 */
export interface Participant {
  id: ID;
  name: string;
  email?: string; // Optional for future contact features
  joinedAt: string; // ISO timestamp
}

/**
 * Split mode for expenses
 */
export enum SplitMode {
  EQUAL = 'equal',
  PERCENTAGE = 'percentage', 
  SHARES = 'shares',
  CUSTOM = 'custom'
}

/**
 * Individual participant's share in an expense
 */
export interface ParticipantShare {
  participantId: ID;
  amount?: number; // For custom splits
  percentage?: number; // For percentage splits  
  shares?: number; // For weighted shares
  isIncluded: boolean; // Whether they participate in this expense
}

/**
 * Single expense entry
 */
export interface Expense {
  id: ID;
  description: string;
  amount: number; // Total amount in cents to avoid floating point issues
  currency: string; // ISO currency code (e.g., 'USD', 'EUR')
  paidBy: ID; // Participant ID who paid
  splitMode: SplitMode;
  participantShares: ParticipantShare[];
  taxAmount?: number; // Optional tax in cents
  tipAmount?: number; // Optional tip in cents
  notes?: string;
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
}

/**
 * Settlement suggestion (who pays whom)
 */
export interface Settlement {
  from: ID; // Participant ID who owes
  to: ID; // Participant ID who should receive
  amount: number; // Amount in cents
}

/**
 * Group status
 */
export enum GroupStatus {
  ACTIVE = 'active',
  SETTLED = 'settled',
  ARCHIVED = 'archived'
}

/**
 * Main Group model - the root of all data
 */
export interface Group {
  // Metadata
  id: ID;
  name: string;
  description?: string;
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
  schemaVersion: string;
  
  // Core data
  participants: Participant[];
  expenses: Expense[];
  
  // Settings
  defaultCurrency: string; // ISO currency code
  defaultSplitMode: SplitMode;
  
  // State
  status: GroupStatus;
  
  // Computed (not persisted, calculated on load)
  balances?: Record<ID, number>; // participantId -> net balance in cents
  settlements?: Settlement[]; // Optimal payment suggestions
}

/**
 * New group creation parameters
 */
export interface CreateGroupParams {
  name: string;
  description?: string;
  defaultCurrency?: string;
  creatorName: string; // Name of the person creating the group
}

/**
 * Add participant parameters
 */
export interface AddParticipantParams {
  name: string;
  email?: string;
}

/**
 * Group creation result
 */
export interface GroupCreationResult {
  group: Group;
  shareUrl: string; // URL for sharing the group
}
